import React, { forwardRef, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const FullscreenOverlay = motion.div

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.5);
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 10px;
    border: 2px solid rgba(0, 0, 0, 0.5);
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
  
  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #4a4a4a rgba(0, 0, 0, 0.5);
  }
`

const ProjectsOverlay = forwardRef(({ isVisible, onClose }, ref) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isVisible])

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <style>{scrollbarStyles}</style>
          <FullscreenOverlay
            ref={ref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleOverlayClick}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.85)',
              zIndex: 10000,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
                duration: 0.4
              }}
              style={{
                background: 'rgba(0, 0, 0, 0.9)',
                color: '#ffffff',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                width: '90%',
                maxWidth: '1200px',
                maxHeight: '85vh',
                position: 'relative',
                fontFamily: 'Gohu, monospace',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Close button - positioned absolute to outer container */}
              <motion.button
                initial={{ opacity: 0, scale: 0.5, rotate: 180 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 150,
                  damping: 12,
                  delay: 0.5
                }}
                whileHover={{ scale: 1.1, opacity: 0.7 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  background: 'transparent',
                  border: 'none',
                  color: '#f0f7f9',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                ×
              </motion.button>

              {/* Scrollable content area */}
              <div
                className="custom-scrollbar"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: window.innerWidth <= 768 ? '20px 15px 30px 15px' : '30px 50px 50px 50px',
                }}
              >

            {/* Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: 0.2,
                staggerChildren: 0.1,
                delayChildren: 0.3
              }}
              style={{
                fontSize: '15px',
                lineHeight: '1.9',
                fontFamily: 'Gohu, monospace',
              }}
            >
              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '24px',
                  marginBottom: '40px',
                  textAlign: 'center',
                  letterSpacing: '3px',
                  color: '#35c19f',
                  textShadow: `
                    0 0 10px rgba(53, 193, 159, 0.4),
                    0 0 20px rgba(53, 193, 159, 0.3),
                    0 0 30px rgba(53, 193, 159, 0.2),
                    0 0 40px rgba(53, 193, 159, 0.1)
                  `,
                  fontWeight: 'normal',
                  filter: 'brightness(1.3)',
                }}
              >
                OUR EXPERIENCE
              </motion.h1>

              {/* About Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                style={{ marginBottom: '40px' }}
              >
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                    delay: 0.1
                  }}
                  style={{
                    marginBottom: '20px',
                    opacity: 0.95,
                    fontFamily: 'Gohu, monospace',
                    fontSize: '14px',
                    lineHeight: '1.9',
                  }}
                >
                  We leverage cutting-edge technology to deliver high-impact solutions across AI workflows, blockchain, 
                  immersive web experiences, and generative art. Our portfolio spans Fortune 500 companies, government agencies, and innovative startups, 
                  where we drive digital transformation through React Three Fiber expertise, AI orchestration, and cloud-native architectures. 
                  From interactive 3D experiences to decentralized applications, we combine creative vision with enterprise-grade execution.

                </motion.p>
              </motion.div>

              {/* Expertise Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: 0.1
                }}
                style={{ marginBottom: '40px' }}
              >
                <motion.h2
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                    delay: 0.2
                  }}
                  style={{
                    fontFamily: 'Gohu, monospace',
                    fontSize: '20px',
                    marginBottom: '20px',
                    color: '#ffffff',
                    fontWeight: 'normal',
                  }}
                >
                  Project Expertise
                </motion.h2>

                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                    delay: 0.3
                  }}
                  style={{ marginBottom: '25px' }}
                >
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                      delay: 0.4
                    }}
                    style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '16px',
                      marginBottom: '10px',
                      color: '#35c19f',
                      fontWeight: 'normal',
                      textShadow: `
                        0 0 10px rgba(53, 193, 159, 0.5),
                        0 0 20px rgba(53, 193, 159, 0.4),
                        0 0 30px rgba(53, 193, 159, 0.3),
                        0 0 40px rgba(53, 193, 159, 0.2)
                      `,
                      filter: 'brightness(1.3)',
                    }}
                  >
                    AI & Machine Learning
                  </motion.h3>
                  <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      staggerChildren: 0.1,
                      delayChildren: 0.5
                    }}
                    style={{
                      margin: '0 0 12px 20px',
                      padding: 0,
                      opacity: 0.95,
                      fontFamily: 'Gohu, monospace',
                      fontSize: '14px',
                      lineHeight: '1.9',
                    }}
                  >
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: "spring", stiffness: 100, damping: 15 }}
                    >
                      Agent marketplaces using Next.js, React, and Python microservices
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: "spring", stiffness: 100, damping: 15 }}
                    >
                      Real-time OCR systems for game analytics
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: "spring", stiffness: 100, damping: 15 }}
                    >
                      Custom tools with LangChain integration
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: "spring", stiffness: 100, damping: 15 }}
                    >
                      Generative AI for animation and content creation
                    </motion.li>
                  </motion.ul>
                </motion.div>

                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    fontFamily: 'Gohu, monospace',
                    fontSize: '16px',
                    marginBottom: '10px',
                    color: '#35c19f',
                    fontWeight: 'normal',
                    textShadow: `
                      0 0 10px rgba(53, 193, 159, 0.5),
                      0 0 20px rgba(53, 193, 159, 0.4),
                      0 0 30px rgba(53, 193, 159, 0.3),
                      0 0 40px rgba(53, 193, 159, 0.2)
                    `,
                    filter: 'brightness(1.3)',
                  }}>
                    Blockchain & Web3
                  </h3>
                  <ul style={{ 
                    margin: '0 0 12px 20px',
                    padding: 0,
                    opacity: 0.95,
                    fontFamily: 'Gohu, monospace',
                    fontSize: '14px',
                    lineHeight: '1.9',
                  }}>
                    <li>Payment rails for fiat-to-crypto conversions</li>
                    <li>NFT platforms and DeFi smart contracts</li>
                    <li>Web3 experiences via Decentraland SDK</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    fontFamily: 'Gohu, monospace',
                    fontSize: '16px',
                    marginBottom: '10px',
                    color: '#35c19f',
                    fontWeight: 'normal',
                    textShadow: `
                      0 0 10px rgba(53, 193, 159, 0.5),
                      0 0 20px rgba(53, 193, 159, 0.4),
                      0 0 30px rgba(53, 193, 159, 0.3),
                      0 0 40px rgba(53, 193, 159, 0.2)
                    `,
                    filter: 'brightness(1.3)',
                  }}>
                    Fintech & Enterprise
                  </h3>
                  <ul style={{ 
                    margin: '0 0 12px 20px',
                    padding: 0,
                    opacity: 0.95,
                    fontFamily: 'Gohu, monospace',
                    fontSize: '14px',
                    lineHeight: '1.9',
                  }}>
                    <li>SaaS platform redesigns with modular systems</li>
                    <li>WORM data management for regulatory compliance</li>
                    <li>90% efficiency gains in reporting optimization</li>
                    <li>Large-scale enterprise integrations</li>
                  </ul>
                </div>
              </motion.div>

              {/* Key Achievements */}
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '20px',
                  marginBottom: '25px',
                  color: '#ffffff',
                  fontWeight: 'normal',
                }}>
                  Project Achievements
                </h2>
                
                {/* Visual Metrics Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px',
                  }}
                >
                  {/* Performance Card */}
                  <motion.div
                    initial={{ opacity: 0, x: -30, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                      delay: 0.1
                    }}
                    style={{
                      border: '1px solid rgba(53, 193, 159, 0.5)',
                      borderRadius: '8px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      boxShadow: '0 0 30px rgba(53, 193, 159, 0.2), inset 0 0 20px rgba(53, 193, 159, 0.05)',
                    }}
                  >
                    <div style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}>
                      Performance
                    </div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 15,
                        delay: 0.2
                      }}
                      style={{
                        fontFamily: 'Gohu, monospace',
                        fontSize: '28px',
                        color: '#35c19f',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        textShadow: `
                          0 0 10px rgba(53, 193, 159, 0.6),
                          0 0 20px rgba(53, 193, 159, 0.4),
                          0 0 30px rgba(53, 193, 159, 0.2)
                        `,
                        filter: 'brightness(1.3)',
                      }}
                    >
                      +90%
                    </motion.div>
                    <div style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '10px',
                      color: 'rgba(255, 255, 255, 0.8)',
                    }}>
                      efficiency gains
                    </div>
                    {/* Mini chart with higher end bar */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: 0.3 }}
                      style={{
                        marginTop: '15px',
                        height: '40px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '3px',
                      }}
                    >
                      {[20, 25, 35, 40, 50, 65, 75, 85, 95].map((height, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          whileInView={{ height: `${height}%` }}
                          viewport={{ once: true, amount: 0.3 }}
                          transition={{
                            type: "spring",
                            stiffness: 60,
                            damping: 15,
                            delay: 0.4 + (i * 0.1)
                          }}
                          style={{
                            flex: 1,
                            background: i === 8 ? '#35c19f' : 'rgba(53, 193, 159, 0.4)',
                            borderRadius: '2px',
                            boxShadow: i === 8 ? '0 0 10px rgba(53, 193, 159, 0.6)' : 'none',
                            filter: i === 8 ? 'brightness(1.3)' : 'brightness(1)',
                          }}
                        />
                      ))}
                    </motion.div>
                  </motion.div>

                  {/* Revenue Card */}
                  <motion.div
                    initial={{ opacity: 0, x: 30, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                      delay: 0.2
                    }}
                    style={{
                      border: '1px solid rgba(53, 193, 159, 0.5)',
                      borderRadius: '8px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      boxShadow: '0 0 30px rgba(53, 193, 159, 0.2), inset 0 0 20px rgba(53, 193, 159, 0.05)',
                    }}
                  >
                    <div style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}>
                      Revenue
                    </div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 15,
                        delay: 0.2
                      }}
                      style={{
                        fontFamily: 'Gohu, monospace',
                        fontSize: '28px',
                        color: '#35c19f',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        textShadow: `
                          0 0 10px rgba(53, 193, 159, 0.6),
                          0 0 20px rgba(53, 193, 159, 0.4),
                          0 0 30px rgba(53, 193, 159, 0.2)
                        `,
                        filter: 'brightness(1.3)',
                      }}
                    >
                      $1.5M+
                    </motion.div>
                    <div style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '10px',
                      color: 'rgba(255, 255, 255, 0.8)',
                    }}>
                      generated
                    </div>
                    {/* Exponential revenue chart */}
                    <motion.svg
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: 0.3 }}
                      style={{ marginTop: '15px', width: '100%', height: '40px' }}
                      viewBox="0 0 100 40"
                      preserveAspectRatio="none"
                    >
                      <motion.path
                        d="M 0,38 L 20,37 L 35,36 L 45,35 L 55,33 Q 65,30 70,26 Q 75,20 80,14 Q 85,8 90,4 Q 95,1 100,0"
                        fill="none"
                        stroke="#35c19f"
                        strokeWidth="1"
                        strokeDasharray="150"
                        initial={{ strokeDashoffset: 150 }}
                        whileInView={{ strokeDashoffset: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                          duration: 2,
                          ease: "easeOut",
                          delay: 0.4
                        }}
                        style={{
                          filter: 'drop-shadow(0 0 10px rgba(53, 193, 159, 0.8))',
                          opacity: 0.9,
                        }}
                      />
                      <motion.path
                        d="M 0,38 L 20,37 L 35,36 L 45,35 L 55,33 Q 65,30 70,26 Q 75,20 80,14 Q 85,8 90,4 Q 95,1 100,0"
                        fill="none"
                        stroke="#35c19f"
                        strokeWidth="0.5"
                        strokeDasharray="150"
                        initial={{ strokeDashoffset: 150 }}
                        whileInView={{ strokeDashoffset: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                          duration: 2,
                          ease: "easeOut",
                          delay: 0.4
                        }}
                        style={{
                          filter: 'blur(3px)',
                          opacity: 0.5,
                        }}
                      />
                      {/* Add gradient fill under the line for more impact */}
                      <defs>
                        <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" style={{ stopColor: '#35c19f', stopOpacity: 0.3 }} />
                          <stop offset="100%" style={{ stopColor: '#35c19f', stopOpacity: 0 }} />
                        </linearGradient>
                      </defs>
                      <motion.path
                        d="M 0,38 L 20,37 L 35,36 L 45,35 L 55,33 Q 65,30 70,26 Q 75,20 80,14 Q 85,8 90,4 Q 95,1 100,0 L 100,40 L 0,40 Z"
                        fill="url(#revenueGradient)"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 0.5 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                          duration: 1,
                          delay: 1
                        }}
                      />
                    </motion.svg>
                  </motion.div>

                  {/* Uptime Card - Now part of the grid */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                      delay: 0.3
                    }}
                    style={{
                      border: '1px solid rgba(53, 193, 159, 0.5)',
                      borderRadius: '8px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      boxShadow: '0 0 30px rgba(53, 193, 159, 0.2), inset 0 0 20px rgba(53, 193, 159, 0.05)',
                    }}
                  >
                    <div style={{
                      fontFamily: 'Gohu, monospace',
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}>
                      Uptime / Zero Downtime
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                          type: "spring",
                          stiffness: 100,
                          damping: 15,
                          delay: 0.2
                        }}
                        style={{
                          fontFamily: 'Gohu, monospace',
                          fontSize: '28px',
                          color: '#35c19f',
                          marginBottom: '8px',
                          fontWeight: 'bold',
                          textShadow: `
                            0 0 10px rgba(53, 193, 159, 0.6),
                            0 0 20px rgba(53, 193, 159, 0.4),
                            0 0 30px rgba(53, 193, 159, 0.2)
                          `,
                          filter: 'brightness(1.3)',
                        }}
                      >
                        100%
                      </motion.div>
                      <div style={{
                        fontFamily: 'Gohu, monospace',
                        fontSize: '10px',
                        color: 'rgba(255, 255, 255, 0.8)',
                      }}>
                        500+ players managed
                      </div>
                    </div>
                    {/* Horizontal progress bar */}
                    <div style={{
                      height: '20px',
                      background: 'rgba(53, 193, 159, 0.1)',
                      border: '1px solid rgba(53, 193, 159, 0.3)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative',
                    }}>
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                          type: "spring",
                          stiffness: 60,
                          damping: 15,
                          delay: 0.5
                        }}
                        style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, #35c19f, #45d1af)',
                          boxShadow: 'inset 0 0 20px rgba(53, 193, 159, 0.3)',
                          filter: 'brightness(1.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span style={{
                          fontFamily: 'Gohu, monospace',
                          fontSize: '9px',
                          color: 'rgba(0, 0, 0, 0.9)',
                          fontWeight: 'bold',
                          letterSpacing: '1px',
                          textShadow: '0 0 3px rgba(255, 255, 255, 0.3)',
                          whiteSpace: 'nowrap',
                        }}>
                          100% UPTIME
                        </span>
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Additional Achievements List */}
                <ul style={{ 
                  margin: '0 0 12px 20px',
                  padding: 0,
                  opacity: 0.95,
                  fontFamily: 'Gohu, monospace',
                  fontSize: '14px',
                  lineHeight: '1.9',
                }}>
                  <li>Won awards from University of Pennsylvania and major hackathons</li>
                  <li>Received grants from Art Council England and ESA</li>
                  <li>Secured major enterprise contracts and partnerships</li>
                </ul>
              </div>

              {/* Tech Stack */}
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '20px',
                  marginBottom: '15px',
                  color: '#ffffff',
                  fontWeight: 'normal',
                }}>
                  Technology Stack
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '15px',
                  fontFamily: 'Gohu, monospace',
                  fontSize: '14px',
                  opacity: 0.95,
                }}>
                  <div>
                    <strong style={{ 
                    color: '#35c19f', 
                    textShadow: `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `,
                    filter: 'brightness(1.1)'
                  }}>Frontend:</strong><br/>
                    React, Next.js, TypeScript, TailwindCSS
                  </div>
                  <div>
                    <strong style={{ 
                    color: '#35c19f', 
                    textShadow: `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `,
                    filter: 'brightness(1.1)'
                  }}>Backend:</strong><br/>
                    Node.js, Python, PostgreSQL, REST APIs
                  </div>
                  <div>
                    <strong style={{ 
                    color: '#35c19f', 
                    textShadow: `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `,
                    filter: 'brightness(1.1)'
                  }}>Cloud:</strong><br/>
                    AWS, Cloudflare, Docker, Serverless
                  </div>
                  <div>
                    <strong style={{ 
                    color: '#35c19f', 
                    textShadow: `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `,
                    filter: 'brightness(1.1)'
                  }}>Emerging:</strong><br/>
                    LangChain, Blockchain SDKs, WebGL
                  </div>
                </div>
              </div>

              {/* Why Us Section */}
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '20px',
                  marginBottom: '15px',
                  color: '#ffffff',
                  fontWeight: 'normal',
                }}>
                  Our Approach
                </h2>
                <p style={{ 
                  marginBottom: '20px',
                  opacity: 0.9,
                  fontFamily: 'Gohu, monospace',
                  fontSize: '14px',
                  lineHeight: '1.9',
                }}>
                  Our projects combine decades of experience in crypto, AI, and emerging technologies. 
                  We deliver end-to-end solutions with enterprise-grade compliance and security, ensuring 
                  each project is scalable, future-proof, and drives measurable business impact through 
                  innovation and technical excellence.
                </p>
              </div>

              {/* Client Logos Section - Placeholder */}
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '18px',
                  marginBottom: '20px',
                  color: '#ffffff',
                  fontWeight: 'normal',
                  textAlign: 'center',
                }}>
                  Past Project Clients
                </h2>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '30px',
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '10px',
                }}>
                  <div style={{
                    fontFamily: 'Gohu, monospace',
                    fontSize: '12px',
                    color: '#35c19f',
                    opacity: 0.9,
                    textAlign: 'center',
                    textShadow: `
                      0 0 10px rgba(53, 193, 159, 0.5),
                      0 0 20px rgba(53, 193, 159, 0.4),
                      0 0 30px rgba(53, 193, 159, 0.3)
                    `,
                    lineHeight: '1.8',
                    filter: 'brightness(1.3)',
                  }}>
                    NASA • MTV • Metallica • KPMG • University of Pennsylvania<br/>
                    European Space Agency • Blink-182 • Playboy • DKNY • Airmiles<br/>
                    US Federal Agencies • KB Homes • Vuse Global • Art Council England
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div style={{
                marginTop: '40px',
                paddingTop: '30px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
              }}>
                <p style={{
                  fontFamily: 'Gohu, monospace',
                  fontSize: '16px',
                  marginBottom: '20px',
                  color: '#35c19f',
                  textShadow: `
                    0 0 10px rgba(53, 193, 159, 0.5),
                    0 0 20px rgba(53, 193, 159, 0.4),
                    0 0 30px rgba(53, 193, 159, 0.3),
                    0 0 40px rgba(53, 193, 159, 0.2)
                  `,
                  filter: 'brightness(1.3)',
                }}>
                  Interested in working with us?
                </p>
                <a 
                  href="mailto:coffeecoded@gmail.com?subject=Project Inquiry"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    background: 'transparent',
                    border: '1px solid #35c19f',
                    color: '#35c19f',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    fontFamily: 'Gohu, monospace',
                    fontSize: '14px',
                    letterSpacing: '1px',
                    textShadow: `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `,
                    boxShadow: `
                      0 0 15px rgba(53, 193, 159, 0.3),
                      0 0 25px rgba(53, 193, 159, 0.2),
                      inset 0 0 10px rgba(53, 193, 159, 0.1)
                    `,
                    filter: 'brightness(1.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(53, 193, 159, 0.1)'
                    e.target.style.boxShadow = `
                      0 0 20px rgba(53, 193, 159, 0.5),
                      0 0 35px rgba(53, 193, 159, 0.4),
                      0 0 50px rgba(53, 193, 159, 0.3),
                      inset 0 0 15px rgba(53, 193, 159, 0.2)
                    `
                    e.target.style.textShadow = `
                      0 0 10px rgba(53, 193, 159, 0.6),
                      0 0 20px rgba(53, 193, 159, 0.5),
                      0 0 30px rgba(53, 193, 159, 0.4)
                    `
                    e.target.style.filter = 'brightness(1.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent'
                    e.target.style.boxShadow = `
                      0 0 15px rgba(53, 193, 159, 0.3),
                      0 0 25px rgba(53, 193, 159, 0.2),
                      inset 0 0 10px rgba(53, 193, 159, 0.1)
                    `
                    e.target.style.textShadow = `
                      0 0 8px rgba(53, 193, 159, 0.5),
                      0 0 16px rgba(53, 193, 159, 0.4),
                      0 0 24px rgba(53, 193, 159, 0.3)
                    `
                    e.target.style.filter = 'brightness(1.3)'
                  }}
                >
                  GET IN TOUCH
                </a>
              </div>
            </motion.div>
              </div>
            </motion.div>
        </FullscreenOverlay>
        </>
      )}
    </AnimatePresence>
  )
})

ProjectsOverlay.displayName = 'ProjectsOverlay'

export default ProjectsOverlay